import { useCallback } from "react";
import { SubscriptionData } from "../api/useWebSockets";
import { getStore } from "../store/store";
import { LiveCall } from "../store/store.types";

type LiveCallRowFromSubscription = {
    id: string;
    session_id: string;
    direction: string;
    state: string;
    agent_extension: string | null;
    agent_user: {
        id: string;
        first_name: string;
        last_name: string;
        avatar: string | null;
    } | null;
    phone_number: string | null;
    caller_name: string | null;
    caller_collection: string | null;
    caller_item_id: number | null;
    started_at: string;
    answered_at: string | null;
    last_event_at: string;
};

const transformRowToLiveCall = (row: LiveCallRowFromSubscription) => {
    const liveCall: LiveCall = {
        id: row.id,
        sessionId: row.session_id,
        direction: row.direction as LiveCall["direction"],
        state: row.state as LiveCall["state"],
        agentExtension: row.agent_extension,
        agentUser: row.agent_user
            ? {
                id: row.agent_user.id,
                firstName: row.agent_user.first_name,
                lastName: row.agent_user.last_name,
                avatar: row.agent_user.avatar,
              }
            : null,
        phoneNumber: row.phone_number,
        callerName: row.caller_name,
        callerCollection: (row.caller_collection ?? null) as LiveCall["callerCollection"],
        callerItemId: row.caller_item_id,
        startedAt: row.started_at,
        answeredAt: row.answered_at,
    };
    return liveCall;
};

export const useLiveCalls = () => {
    const handleLiveCallsEvent = useCallback((subscriptionData: SubscriptionData) => {
        const event = subscriptionData.event;
        const data = subscriptionData.data;
        const liveCallsActions = getStore().liveCalls;

        if (event === "init") {
            const rows = Array.isArray(data) ? (data as Array<LiveCallRowFromSubscription>) : [];
            const calls = rows.map((row) => transformRowToLiveCall(row));
            liveCallsActions.reset(calls);
            return;
        }

        if (event === "create" || event === "update") {
            const rows = Array.isArray(data) ? (data as Array<LiveCallRowFromSubscription>) : [];
            for (const row of rows) {
                const liveCall = transformRowToLiveCall(row);
                liveCallsActions.upsert(liveCall);
            }
            return;
        }

        if (event === "delete") {
            const idsToRemove = Array.isArray(data) ? (data as Array<string>) : [];
            for (const id of idsToRemove) {
                liveCallsActions.removeById(id);
            }
            return;
        }
    }, []);

    const hookApi = { handleLiveCallsEvent };
    return hookApi;
};
